import { useEffect, useRef, useState, type SyntheticEvent } from "react";

/*
 * Sandboxed renderer for (already sanitized) email HTML.
 *
 * The sandbox grants `allow-same-origin` but never `allow-scripts`: nothing in
 * the email can execute even if DOMPurify were bypassed, forms can't submit,
 * and the frame can't navigate the app. Same-origin only lets *us* read the
 * frame's height. The sender's CSS is confined to this document, so <style> is
 * allowed back. The CSP is a second lock: no scripts, no frames, no external stylesheets.
 */
const CSP = "default-src 'none'; img-src https: data:; style-src 'unsafe-inline'; font-src https: data:";

const SHELL_CSS = `
html { color-scheme: light; overflow-y: hidden; }
body { margin: 0; color: #1a1a1a; background: #fff; font: 14px/1.65 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; overflow-wrap: anywhere; overflow-x: auto; }
img { max-width: 100%; height: auto; }
a { color: #2563eb; }
blockquote { margin: 0 0 0 .5em; padding-left: 1em; border-left: 3px solid #d1d5db; color: #4b5563; }
[style*="position:fixed" i], [style*="position: fixed" i] { position: static !important; }
`;

function shell(html: string): string {
    return `<!doctype html><html><head><meta charset="utf-8">`
        + `<meta http-equiv="Content-Security-Policy" content="${CSP}">`
        + `<meta name="referrer" content="no-referrer"><base target="_blank">`
        + `<style>${SHELL_CSS}</style></head><body>${html}</body></html>`;
}

export function EmailFrame({ html, className, title = "Email content" }: { html: string; className?: string; title?: string }) {
    const [height, setHeight] = useState(0);
    const observer = useRef<ResizeObserver | null>(null);
    useEffect(() => () => observer.current?.disconnect(), []);

    // Fires again whenever srcDoc changes; images loading later resize the body.
    const onLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
        const doc = event.currentTarget.contentDocument;
        if (!doc) return;
        const measure = () => setHeight(doc.documentElement.scrollHeight);
        measure();
        observer.current?.disconnect();
        observer.current = new ResizeObserver(measure);
        observer.current.observe(doc.body);
    };

    return (
        <iframe
            onLoad={onLoad}
            className={className}
            title={title}
            srcDoc={shell(html)}
            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
            referrerPolicy="no-referrer"
            style={{ display: "block", width: "100%", height, border: 0 }}
        />
    );
}
